import React, { useMemo } from "react"
import { useQuery } from "@apollo/client"
import { useTranslation } from "react-i18next"
import PPUtils from "../../utils/PPUtils"
import {
    DematchingKpiSkeleton,
    DematchingPanelSkeleton,
    DematchingStatsPopIn
} from "./PPDematchingStatsSkeleton"
import {
    QUERY_DEMATCHING_COVERAGE_OVERVIEW,
    QUERY_DEMATCHING_PRODUCER_COVERAGE_STATS
} from "../../utils/dematching/dematchingQueries"

const PP_STATS_COLORS = {
    primary: PPUtils.Colors.NeutralStart,
    primaryDark: PPUtils.Colors.NeutralEnd,
    muted: PPUtils.Colors.GreyLight
}

const PP_NEUTRAL_CHART_COLORS = [
    PP_STATS_COLORS.primary,
    "#5a9fd4",
    "#4d7fb8",
    PP_STATS_COLORS.primaryDark,
    "#6c8ebf",
    "#7a9ec8",
    "#3d6fa5",
    "#89a7c7",
    "#5c7cfa",
    "#6889b8",
    "#9bb5d1",
    "#2f5f9e"
]

function chartColor(index) {
    return PP_NEUTRAL_CHART_COLORS[index % PP_NEUTRAL_CHART_COLORS.length]
}

function formatCoveragePercent(value) {
    return value > 0 ? `${value} %` : "–"
}

function toBarItem(item, name) {
    return {
        key: item.key ?? name,
        name,
        matchedCount: item.matchedCount,
        totalCount: item.totalCount ?? item.totalProductCount,
        coveragePercent: item.coveragePercent,
        colorHex: item.colorHex
    }
}

function formatBarPercent(value) {
    return value > 0 ? `${value}%` : ""
}

function CoverageBarTable({ items, colorFor }) {
    return (
        <ul className="pp-dematching-coverage-bars">
            {items.map((item, index) => {
                const total = item.totalCount ?? 0
                const width = total > 0 ? Math.min(100, item.coveragePercent) : 0
                const barColor = colorFor(item, index)
                const showBarLabel = width >= 14

                return (
                    <li key={item.key} className="pp-dematching-coverage-bars__row">
                        <div className="pp-dematching-coverage-bars__head">
                            <span className="pp-dematching-coverage-bars__name" title={item.name}>
                                {item.name}
                            </span>
                            <span className="pp-dematching-coverage-bars__value">
                                {formatCoveragePercent(item.coveragePercent)}
                                <span className="pp-dematching-coverage-bars__fraction">
                                    ({item.matchedCount}/{total})
                                </span>
                            </span>
                        </div>
                        <div className="pp-dematching-coverage-bars__track" aria-hidden="true">
                            <div
                                className="pp-dematching-coverage-bars__fill"
                                style={{ width: `${width}%`, background: barColor }}
                            >
                                {showBarLabel && (
                                    <span className="pp-dematching-coverage-bars__fill-label">
                                        {formatBarPercent(item.coveragePercent)}
                                    </span>
                                )}
                            </div>
                        </div>
                    </li>
                )
            })}
        </ul>
    )
}

function KpiCard({ label, value, sublabel, accent }) {
    return (
        <div className="pp-matching-stats-kpi">
            <div className="pp-matching-stats-kpi__accent" style={{ background: accent }} />
            <div className="pp-matching-stats-kpi__body">
                <div className="pp-matching-stats-kpi__label">{label}</div>
                <div className="pp-matching-stats-kpi__value">{value}</div>
                {sublabel && <div className="pp-matching-stats-kpi__sub">{sublabel}</div>}
            </div>
        </div>
    )
}

function StatsPanel({ title, children }) {
    return (
        <div className="pp-matching-stats-panel">
            <div className="pp-matching-stats-panel__title">{title}</div>
            {children}
        </div>
    )
}

function StatsScope({ label, hint, children }) {
    return (
        <section className="pp-dematching-stats-scope">
            <div className="pp-dematching-stats-scope__head">
                <div className="pp-dematching-stats-scope__label">{label}</div>
                {hint && <div className="pp-dematching-stats-scope__hint">{hint}</div>}
            </div>
            {children}
        </section>
    )
}

export default function PPDematchingStatsSidebar({ shopId, shopQuickInfo, isNarrowLayout, onCollapse }) {
    const [t] = useTranslation()

    const skip = shopId === null || shopId === undefined

    const { data: overviewData, loading: overviewLoading } = useQuery(QUERY_DEMATCHING_COVERAGE_OVERVIEW, {
        variables: { shopId },
        skip,
        fetchPolicy: "cache-first"
    })

    const { data: producerData, loading: producerLoading } = useQuery(QUERY_DEMATCHING_PRODUCER_COVERAGE_STATS, {
        variables: { shopId },
        skip,
        fetchPolicy: "cache-first"
    })

    const overview = overviewData?.dematchingCoverageOverview
    const customerAssortment = overview?.customerAssortment
    const shopCoverages = overview?.shopCoverages ?? []
    const producerStats = producerData?.dematchingProducerCoverageStats
    const categoryCoverages = producerStats?.categoryCoverages ?? []
    const producerCoverages = producerStats?.producerCoverages ?? []

    const selectedShop = useMemo(
        () => shopCoverages.find((s) => s.id === shopId) ?? null,
        [shopCoverages, shopId]
    )

    const shopComparison = useMemo(
        () => [...shopCoverages].sort((a, b) => b.coveragePercent - a.coveragePercent),
        [shopCoverages]
    )

    const categoryComparison = useMemo(
        () => [...categoryCoverages].sort((a, b) => b.coveragePercent - a.coveragePercent),
        [categoryCoverages]
    )

    const shopBarItems = useMemo(
        () =>
            shopComparison.map((s) =>
                toBarItem(
                    { ...s, key: `${s.id}-${s.lang}` },
                    `${s.name} ${s.lang.toUpperCase()}`
                )
            ),
        [shopComparison]
    )

    const categoryBarItems = useMemo(
        () => categoryComparison.map((c) => toBarItem({ ...c, key: c.name }, c.name)),
        [categoryComparison]
    )

    const producerBarItems = useMemo(
        () => producerCoverages.map((p) => toBarItem({ ...p, key: p.name }, p.name)),
        [producerCoverages]
    )

    const shopBarColor = (item, index) => item.colorHex || chartColor(index)
    const paletteBarColor = (_item, index) => chartColor(index)

    const customerKpiReady = !!customerAssortment
    const shopChartReady = shopComparison.length > 1
    const categoryChartReady = categoryComparison.length > 0
    const producerChartReady = producerCoverages.length > 0

    const shopScopeLabel = selectedShop
        ? `${selectedShop.name} ${selectedShop.lang.toUpperCase()}`
        : shopQuickInfo
          ? `${shopQuickInfo.name} ${shopQuickInfo.lang.toUpperCase()}`
          : t("dematching.stats.scope_shop")

    if (skip) {
        return null
    }

    return (
        <aside className="pp-matching-page__stats">
            <div className="pp-matching-stats">
                <div className="pp-matching-stats__header">
                    {isNarrowLayout && (
                        <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm pp-matching-stats__back-btn"
                            onClick={onCollapse}
                        >
                            <i className="fas fa-chevron-left" aria-hidden="true" />
                            <span>{t("dematching.back_to_list")}</span>
                        </button>
                    )}
                    <h4 className="pp-matching-stats__title">{t("dematching.stats.title")}</h4>
                </div>

                {shopChartReady ? (
                    <StatsScope
                        label={t("dematching.stats.scope_portfolio")}
                        hint={t("dematching.stats.scope_portfolio_hint")}
                    >
                        <DematchingStatsPopIn>
                            <StatsPanel title={t("dematching.stats.chart_shop_coverage")}>
                                <CoverageBarTable items={shopBarItems} colorFor={shopBarColor} />
                            </StatsPanel>
                        </DematchingStatsPopIn>
                    </StatsScope>
                ) : overviewLoading ? (
                    <DematchingPanelSkeleton height={160} />
                ) : null}

                <StatsScope label={shopScopeLabel}>
                    <div className="pp-matching-stats-kpi-grid">
                    {customerKpiReady ? (
                        <DematchingStatsPopIn>
                            <KpiCard
                                label={t("dematching.stats.customer_assortment")}
                                value={formatCoveragePercent(customerAssortment.coveragePercent)}
                                sublabel={t("dematching.stats.customer_assortment_hint", {
                                    matched: customerAssortment.matchedCount,
                                    total: customerAssortment.totalCount
                                })}
                                accent={selectedShop?.colorHex || PP_STATS_COLORS.primary}
                            />
                        </DematchingStatsPopIn>
                    ) : (
                        <DematchingKpiSkeleton />
                    )}
                </div>

                {producerLoading && !categoryChartReady ? (
                    <DematchingPanelSkeleton height={140} />
                ) : categoryChartReady ? (
                    <DematchingStatsPopIn>
                        <StatsPanel title={t("dematching.stats.chart_category_coverage")}>
                            <CoverageBarTable items={categoryBarItems} colorFor={paletteBarColor} />
                        </StatsPanel>
                    </DematchingStatsPopIn>
                ) : null}

                {producerLoading && !producerChartReady ? (
                    <DematchingPanelSkeleton height={140} />
                ) : producerChartReady ? (
                    <DematchingStatsPopIn>
                        <StatsPanel title={t("dematching.stats.chart_producer_coverage")}>
                            <CoverageBarTable items={producerBarItems} colorFor={paletteBarColor} />
                        </StatsPanel>
                    </DematchingStatsPopIn>
                ) : null}
                </StatsScope>
            </div>
        </aside>
    )
}
